import { formatCount } from '../../../../utils/sms';

/** A member count while it loads (undefined), once known (number) or when it failed (null). */
export type CountState = number | null | undefined;

/** "REAL_ESTATE" → "Real estate". */
export function humanizeEnum(value: string | null | undefined): string {
  const words = (value ?? '').toString().replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/** "1 contact", "1,204 contacts". */
export function countNoun(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

/**
 * Ticked rows, kept by uuid so a selection survives paging and a refreshed
 * list (new objects for the same records).
 */
export class GroupSelection<T extends { uuid?: string | null; id?: string | number | null }> {
  private picked = new Map<string, T>();

  private key(item: T): string {
    return String(item?.uuid ?? item?.id ?? '');
  }

  get items(): T[] {
    return Array.from(this.picked.values());
  }

  get size(): number {
    return this.picked.size;
  }

  has(item: T): boolean {
    return this.picked.has(this.key(item));
  }

  toggle(item: T): void {
    const key = this.key(item);
    if (!key) return;
    if (this.picked.has(key)) this.picked.delete(key);
    else this.picked.set(key, item);
  }

  allOn(page: T[]): boolean {
    return page.length > 0 && page.every((item) => this.has(item));
  }

  someOn(page: T[]): boolean {
    return page.some((item) => this.has(item)) && !this.allOn(page);
  }

  /** Ticks every row on the page, or unticks them when all are ticked already. */
  togglePage(page: T[]): void {
    if (this.allOn(page)) {
      page.forEach((item) => this.picked.delete(this.key(item)));
    } else {
      page.forEach((item) => {
        const key = this.key(item);
        if (key) this.picked.set(key, item);
      });
    }
  }

  /** Keeps ticked rows in step with a freshly loaded page (same uuid, newer data). */
  refresh(page: T[]): void {
    page.forEach((item) => {
      const key = this.key(item);
      if (this.picked.has(key)) this.picked.set(key, item);
    });
  }

  remove(items: T[]): void {
    items.forEach((item) => this.picked.delete(this.key(item)));
  }

  clear(): void {
    this.picked.clear();
  }
}

/** Sum of known member counts, and whether any count is still unknown. */
export function sumCounts(counts: CountState[]): { total: number; complete: boolean } {
  let total = 0;
  let complete = true;
  for (const count of counts) {
    if (typeof count === 'number') total += count;
    else complete = false;
  }
  return { total, complete };
}
